import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useSession } from '../../contexts/SessionContext';
import Modal from '../ui/Modal';
import ModalEnviarEmailCliente from '../ui/ModalEnviarEmailCliente';
import InputDescripcionConOrtografia from '../ui/InputDescripcionConOrtografia';
import { FiMail, FiEdit2, FiSave, FiCalendar, FiCheckCircle, FiClock, FiXCircle } from 'react-icons/fi';
import PresupuestoSession from './PresupuestoSession';
import FacturaSession from './FacturaSession';
import ProyectoSession from './ProyectoSession';
import EmailLogSession from './EmailLogSession';
import { useAuth } from '../../contexts/AuthContext';
import { puede } from '../../utils/permisos';

const SUB_TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'citas', label: 'Citas' },
  { id: 'presupuestos', label: 'Presupuestos' },
  { id: 'tareas', label: 'Tareas' },
  { id: 'facturas', label: 'Facturas' },
  { id: 'proyectos', label: 'Proyectos' },
  { id: 'notas', label: 'Notas' },
  { id: 'correos', label: 'Correos' },
];

const BADGE_CRM = {
  prospecto: 'badge',
  contactado: 'badge-info',
  presupuesto_enviado: 'badge-warning',
  negociando: 'badge-warning',
  ganado: 'badge-ok',
  perdido: 'badge-danger',
};

const ETIQUETA_CRM = {
  prospecto: 'Prospecto',
  contactado: 'Contactado',
  presupuesto_enviado: 'Presupuesto enviado',
  negociando: 'Negociando',
  ganado: 'Ganado',
  perdido: 'Perdido',
};

const ESTADOS_CITA = {
  programada: { etiqueta: 'Programada', icono: FiClock, clase: 'badge-info' },
  confirmada: { etiqueta: 'Confirmada', icono: FiCheckCircle, clase: 'badge-ok' },
  completada: { etiqueta: 'Completada', icono: FiCheckCircle, clase: 'badge-ok' },
  cancelada: { etiqueta: 'Cancelada', icono: FiXCircle, clase: 'badge-danger' },
};

function fmt(v) {
  if (v == null) return '—';
  return v || '—';
}

function fmtFecha(f) {
  if (!f) return '—';
  return new Date(f).toLocaleDateString('es-ES');
}

function fmtEuro(v) {
  return Number(v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

function NuevoPresupuestoSession({ sesion, cliente, onGuardado }) {
  const [notas, setNotas] = useState('');
  const [lineas, setLineas] = useState([{ descripcion: '', cantidad: 1, precioUnitario: 0, iva: 21 }]);
  const [guardando, setGuardando] = useState(false);

  const actualizarLinea = (indice, campo, valor) => setLineas((actuales) => actuales.map((linea, i) => i === indice ? { ...linea, [campo]: valor } : linea));
  const guardar = async (e) => {
    e.preventDefault();
    const lineasValidas = lineas.filter((linea) => String(linea.descripcion || '').trim());
    if (!lineasValidas.length) { alert('Añade al menos una línea al presupuesto.'); return; }
    setGuardando(true);
    try {
      await api.post('/presupuestos', {
        clienteId: sesion.clienteId || sesion.entityId,
        notas,
        lineas: lineasValidas.map((linea) => ({ ...linea, cantidad: Number(linea.cantidad) || 1, precioUnitario: Number(linea.precioUnitario) || 0, iva: Number(linea.iva) || 0 })),
      });
      onGuardado();
    } catch (err) { alert(err.response?.data?.error || 'No se pudo crear el presupuesto.'); }
    finally { setGuardando(false); }
  };

  return (
    <div className="session-content">
      <div className="session-panel" style={{ marginBottom: 16 }}><div className="session-panel-title">Cliente</div><strong>{cliente.nombre}</strong><div className="muted">{cliente.email || cliente.telefono || 'Datos de contacto disponibles en la ficha'}</div></div>
      <form onSubmit={guardar} className="form-grid">
        <div className="span-2"><strong>Líneas del presupuesto</strong></div>
        {lineas.map((linea, indice) => (
          <div className="span-2 presupuesto-linea-form" key={indice} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) 90px 120px 90px', gap: 8, alignItems: 'end' }}>
            <label className="field presupuesto-linea-descripcion"><span>Descripción</span><InputDescripcionConOrtografia required value={linea.descripcion} onChange={(val) => actualizarLinea(indice, 'descripcion', val)} /></label>
            <label className="field"><span>Cantidad</span><input type="number" min="0" step="any" value={linea.cantidad} onChange={(e) => actualizarLinea(indice, 'cantidad', e.target.value)} /></label>
            <label className="field"><span>Precio</span><input type="number" min="0" step="0.01" value={linea.precioUnitario} onChange={(e) => actualizarLinea(indice, 'precioUnitario', e.target.value)} /></label>
            <label className="field"><span>IVA %</span><input type="number" min="0" step="1" value={linea.iva} onChange={(e) => actualizarLinea(indice, 'iva', e.target.value)} /></label>
          </div>
        ))}
        <button type="button" className="btn span-2" onClick={() => setLineas((actuales) => [...actuales, { descripcion: '', cantidad: 1, precioUnitario: 0, iva: 21 }])}>Añadir línea</button>
        <label className="field span-2"><span>Notas</span><textarea rows={3} value={notas} onChange={(e) => setNotas(e.target.value)} /></label>
        <div className="form-actions span-2"><button type="submit" className="btn btn-primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Crear presupuesto'}</button></div>
      </form>
    </div>
  );
}

export default function ClienteSession({ sesion }) {
  const { cambiarSubTab, abrirItemTab, cerrarItemTab, irInicio } = useSession();
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const puedeCrearPresupuesto = puede(usuario, 'presupuestos', 'crear');
  const puedeCrearTarea = puede(usuario, 'tareas', 'crear');
  const subTab = sesion.subTab || 'resumen';

  const [cliente, setCliente] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [presupuestos, setPresupuestos] = useState([]);
  const [citas, setCitas] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [cargandoSub, setCargandoSub] = useState(false);

  const [notas, setNotas] = useState('');
  const [guardandoNotas, setGuardandoNotas] = useState(false);
  const [notasOk, setNotasOk] = useState(false);

  const [emails, setEmails] = useState([]);
  const [cargandoEmails, setCargandoEmails] = useState(false);
  const [emailsOk, setEmailsOk] = useState(null);
  const [emailsError, setEmailsError] = useState(null);

  const [modalEmailAbierto, setModalEmailAbierto] = useState(false);
  const [emailForm, setEmailForm] = useState({ para: '', asunto: '', cuerpoTexto: '', tipo: 'manual', html: false });
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [emailFormError, setEmailFormError] = useState(null);
  const [editandoCliente, setEditandoCliente] = useState(false);
  const [clienteEditado, setClienteEditado] = useState(null);
  const [guardandoCliente, setGuardandoCliente] = useState(false);
  const [tareas, setTareas] = useState([]);

  const cargarCliente = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/clientes/${sesion.entityId}`);
      setCliente(data);
      setNotas(data.notas || '');
    } catch {
      setError('No se pudo cargar el cliente.');
    } finally {
      setCargando(false);
    }
  }, [sesion.entityId]);

  useEffect(() => { cargarCliente(); }, [cargarCliente]);

  const cargarSubTab = useCallback(async (tab) => {
    if (tab === 'resumen' || tab === 'notas') return;
    setCargandoSub(true);
    try {
      if (tab === 'presupuestos') {
        const { data } = await api.get(`/presupuestos?clienteId=${sesion.entityId}`);
        setPresupuestos(Array.isArray(data) ? data : data.docs || []);
      } else if (tab === 'citas') {
        const { data } = await api.get(`/citas?clienteId=${sesion.entityId}`);
        const citasCliente = Array.isArray(data) ? data : data.docs || [];
        setCitas(citasCliente.sort((a, b) => new Date(b.inicio || b.createdAt).getTime() - new Date(a.inicio || a.createdAt).getTime()));
      } else if (tab === 'facturas') {
        const { data } = await api.get(`/facturas?clienteId=${sesion.entityId}`);
        setFacturas(Array.isArray(data) ? data : data.docs || []);
      } else if (tab === 'proyectos') {
        const { data } = await api.get(`/proyectos?clienteId=${sesion.entityId}`);
        setProyectos(Array.isArray(data) ? data : data.docs || []);
      } else if (tab === 'tareas') {
        const { data } = await api.get(`/tareas?clienteId=${sesion.entityId}`);
        setTareas(Array.isArray(data) ? data : data.docs || []);
      } else if (tab === 'correos') {
        setCargandoEmails(true);
        try {
          const { data } = await api.get(`/email-logs?clienteId=${sesion.entityId}`);
          setEmails(Array.isArray(data) ? data : data.docs || []);
        } catch {
          // silencio
        } finally {
          setCargandoEmails(false);
        }
      }
    } catch {
      // silencio — mostramos vacío
    } finally {
      setCargandoSub(false);
    }
  }, [sesion.entityId]);

  const [resumenData, setResumenData] = useState({ presupuestos: [], facturas: [] });
  useEffect(() => {
    if (subTab !== 'resumen') { cargarSubTab(subTab); return; }
    // Cargar datos para el panel de actividad reciente y KPIs
    Promise.all([
      api.get(`/presupuestos?clienteId=${sesion.entityId}`).catch(() => ({ data: [] })),
      api.get(`/facturas?clienteId=${sesion.entityId}`).catch(() => ({ data: [] })),
    ]).then(([pRes, fRes]) => {
      const pArr = Array.isArray(pRes.data) ? pRes.data : pRes.data.docs || [];
      const fArr = Array.isArray(fRes.data) ? fRes.data : fRes.data.docs || [];
      setResumenData({ presupuestos: pArr, facturas: fArr });
    });
  }, [subTab, sesion.entityId, cargarSubTab]);

  const onGuardarNotas = async () => {
    setGuardandoNotas(true);
    try {
      await api.put(`/clientes/${sesion.entityId}`, { notas });
      setNotasOk(true);
      setTimeout(() => setNotasOk(false), 2500);
    } catch {
      alert('Error al guardar notas');
    } finally {
      setGuardandoNotas(false);
    }
  };

  const abrirEdicionCliente = () => { setClienteEditado({ ...cliente }); setEditandoCliente(true); };
  const guardarCliente = async (e) => {
    e.preventDefault();
    setGuardandoCliente(true);
    try {
      const { data } = await api.put(`/clientes/${sesion.entityId}`, clienteEditado);
      setCliente(data);
      setNotas(data.notas || '');
      setEditandoCliente(false);
    } catch (err) { alert(err.response?.data?.error || 'No se pudo guardar el cliente.'); }
    finally { setGuardandoCliente(false); }
  };

  const cargarEmails = useCallback(async () => {
    setCargandoEmails(true);
    try {
      const { data } = await api.get(`/email-logs?clienteId=${sesion.entityId}`);
      setEmails(Array.isArray(data) ? data : data.docs || []);
    } catch {
      // silencio
    } finally {
      setCargandoEmails(false);
    }
  }, [sesion.entityId]);

  const abrirRutaDesdeSesion = (ruta) => {
    irInicio();
    navigate(ruta);
  };

  const onAbrirModalEmail = () => {
    setEmailForm({ para: cliente?.email || '', asunto: '', cuerpoTexto: '', tipo: 'manual', html: false });
    setEmailFormError(null);
    setModalEmailAbierto(true);
  };

  const onEnviarEmail = async (e) => {
    e.preventDefault();
    setEnviandoEmail(true);
    setEmailFormError(null);
    try {
      const cuerpoHtml = emailForm.html
        ? `<div>${emailForm.cuerpoTexto.replace(/\n/g, '<br>')}</div>`
        : undefined;
      await api.post('/email-logs/enviar', {
        clienteId: sesion.entityId,
        para: emailForm.para,
        asunto: emailForm.asunto,
        cuerpoTexto: emailForm.cuerpoTexto,
        cuerpoHtml,
        tipo: emailForm.tipo,
      });
      setModalEmailAbierto(false);
      setEmailsOk('Email enviado correctamente.');
      setTimeout(() => setEmailsOk(null), 3000);
      cargarEmails();
    } catch (err) {
      setEmailFormError(err.response?.data?.error || 'Error al enviar el email.');
    } finally {
      setEnviandoEmail(false);
    }
  };

  if (cargando) return <div className="session-content"><p className="empty">Cargando…</p></div>;
  if (error) return <div className="session-content"><div className="alert-error">{error}</div></div>;
  if (!cliente) return null;

  const totalFacturado = resumenData.facturas.reduce((s, f) => s + Number(f.total || 0), 0);

  const SubTabBar = () => (
    <>
      {/* Barra principal: tabs estáticos de sección */}
      <div className="session-subtabs">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            className={`session-subtab ${subTab === t.id ? 'session-subtab--active' : ''}`}
            onClick={() => cambiarSubTab(sesion.id, t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Barra secundaria: documentos abiertos anclados, solo si los hay */}
      {(sesion.itemTabs || []).length > 0 && (
        <div className="session-subtabs session-subtabs--items">
          {(sesion.itemTabs || []).map(t => (
            <span key={t.id} className={`session-subtab session-subtab--item ${subTab === t.id ? 'session-subtab--active' : ''}`}>
              <button onClick={() => cambiarSubTab(sesion.id, t.id)}>{t.titulo}</button>
              <button className="session-tab-close" onClick={(e) => { e.stopPropagation(); cerrarItemTab(sesion.id, t.id); }}>×</button>
            </span>
          ))}
        </div>
      )}
    </>
  );

  const itemTabActiva = (sesion.itemTabs || []).find(t => t.id === subTab);
  if (itemTabActiva) {
    const sesionItem = { ...itemTabActiva, subTab: 'resumen' };
    const Comp =
      itemTabActiva.tipo === 'presupuesto' ? PresupuestoSession :
      itemTabActiva.tipo === 'presupuesto-nuevo' ? NuevoPresupuestoSession :
      itemTabActiva.tipo === 'factura'     ? FacturaSession :
      itemTabActiva.tipo === 'proyecto'    ? ProyectoSession :
      itemTabActiva.tipo === 'email'       ? EmailLogSession : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <SubTabBar />
        <div style={{ flex: 1, overflow: 'auto' }}>
          {Comp && <Comp sesion={sesionItem} cliente={cliente} onGuardado={() => { cambiarSubTab(sesion.id, 'presupuestos'); cargarSubTab('presupuestos'); }} />}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SubTabBar />
      <div style={{ flex: 1, overflow: 'auto' }}>

      {/* Resumen */}
      {subTab === 'resumen' && (
        <div className="session-panels">
          {/* Datos de contacto */}
          <div className="session-panel">
            <div className="session-panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>Datos de contacto <button className="icon-btn" onClick={abrirEdicionCliente} title="Editar cliente"><FiEdit2 /></button></div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <tbody>
                {[
                  ['Nombre', cliente.nombre],
                  ['NIF', cliente.nif],
                  ['Email', cliente.email],
                  ['Teléfono', cliente.telefono],
                  ['Dirección', cliente.direccion],
                  ['Población', cliente.poblacion],
                ].map(([l, v]) => (
                  <tr key={l}>
                    <td style={{ color: 'var(--text-soft)', paddingBottom: 6, paddingRight: 12, fontWeight: 600, fontSize: 12 }}>{l}</td>
                    <td style={{ paddingBottom: 6 }}>{fmt(v)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ color: 'var(--text-soft)', paddingBottom: 6, paddingRight: 12, fontWeight: 600, fontSize: 12 }}>Estado CRM</td>
                  <td style={{ paddingBottom: 6 }}>
                    {cliente.estadoCrm
                      ? <span className={`badge ${BADGE_CRM[cliente.estadoCrm] || 'badge'}`}>{ETIQUETA_CRM[cliente.estadoCrm] || cliente.estadoCrm}</span>
                      : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
            {cliente.email && puede(usuario, 'clientes', 'enviar_email') && (
              <button
                className="btn btn-sm btn-primary"
                style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center' }}
                onClick={onAbrirModalEmail}
              >
                <FiMail /> Contactar
              </button>
            )}
          </div>

          {/* Próximo contacto */}
          <div className="session-panel">
            <div className="session-panel-title">Próximo contacto</div>
            <div className="session-kpi">{cliente.proximoContacto ? fmtFecha(cliente.proximoContacto) : '—'}</div>
            <div className="session-kpi-label">{cliente.proximoContacto ? 'Fecha programada' : 'Sin programar'}</div>
          </div>

          {/* Personas de contacto */}
          {Array.isArray(cliente.personasContacto) && cliente.personasContacto.length > 0 && (
            <div className="session-panel" style={{ gridColumn: 'span 2' }}>
              <div className="session-panel-title">Personas de contacto ({cliente.personasContacto.length})</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginTop: 8 }}>
                {cliente.personasContacto.map((pc, idx) => (
                  <div key={idx} style={{ background: 'var(--bg-body, #f8fafc)', border: '1px solid var(--border, #e2e8f0)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{pc.nombre} {pc.apellidos}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>📞 {pc.telefono || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>✉️ {pc.email || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* KPIs */}
          <div className="session-panel">
            <div className="session-panel-title">KPIs</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div className="session-kpi">{fmtEuro(totalFacturado)}</div>
                <div className="session-kpi-label">Total facturado</div>
              </div>
              <div style={{ display: 'flex', gap: 24 }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{resumenData.presupuestos.length}</div>
                  <div className="session-kpi-label">Presupuestos</div>
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{resumenData.facturas.length}</div>
                  <div className="session-kpi-label">Facturas</div>
                </div>
              </div>
            </div>
          </div>

          {/* Panel: Embudo de conversión */}
          {(() => {
            const pres = resumenData.presupuestos;
            const total = pres.length || 1;
            const aceptados  = pres.filter(p => p.estado === 'aceptado' || p.estado === 'convertido').length;
            const enviados   = pres.filter(p => p.estado === 'enviado').length;
            const rechazados = pres.filter(p => p.estado === 'rechazado').length;
            const tasa = Math.round((aceptados / total) * 100);
            const bars = [
              { label: 'Enviados',   n: enviados,   color: '#3b82f6' },
              { label: 'Aceptados',  n: aceptados,  color: '#22c55e' },
              { label: 'Rechazados', n: rechazados, color: '#ef4444' },
            ];
            const max = Math.max(...bars.map(b => b.n), 1);
            return (
              <div className="session-panel">
                <div className="session-panel-title">Conversión de presupuestos</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                  {bars.map(b => (
                    <div key={b.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                        <span style={{ color: 'var(--text-soft)' }}>{b.label}</span>
                        <span style={{ fontWeight: 600 }}>{b.n}</span>
                      </div>
                      <div style={{ background: '#f1f5f9', borderRadius: 4, height: 8 }}>
                        <div style={{ width: `${(b.n / max) * 100}%`, background: b.color, height: 8, borderRadius: 4, transition: 'width .4s' }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14, textAlign: 'center' }}>
                  <span style={{ fontSize: 28, fontWeight: 800, color: tasa >= 50 ? '#22c55e' : '#f59e0b' }}>{tasa}%</span>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>tasa de cierre</div>
                </div>
              </div>
            );
          })()}

          {/* Panel: Historial de facturación por mes (últimos 6 meses) */}
          {(() => {
            const meses = Array.from({ length: 6 }, (_, i) => {
              const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - (5 - i));
              return { label: d.toLocaleString('es-ES', { month: 'short' }), year: d.getFullYear(), month: d.getMonth() };
            });
            const totales = meses.map(m =>
              resumenData.facturas.filter(f => {
                const d = new Date(f.fecha || f.createdAt);
                return d.getMonth() === m.month && d.getFullYear() === m.year;
              }).reduce((s, f) => s + Number(f.total || 0), 0)
            );
            const max = Math.max(...totales, 1);
            return (
              <div className="session-panel">
                <div className="session-panel-title">Facturación últimos 6 meses</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80, marginTop: 12 }}>
                  {totales.map((v, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ width: '100%', background: v > 0 ? '#6366f1' : '#f1f5f9', borderRadius: '4px 4px 0 0', height: `${Math.max((v / max) * 68, v > 0 ? 4 : 0)}px`, transition: 'height .4s' }} title={`${meses[i].label}: ${v.toFixed(0)} €`} />
                      <span style={{ fontSize: 10, color: 'var(--text-soft)' }}>{meses[i].label}</span>
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: 'var(--text-soft)' }}>
                  Total: <strong>{resumenData.facturas.reduce((s, f) => s + Number(f.total || 0), 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</strong>
                </div>
              </div>
            );
          })()}

          {/* Actividad reciente */}
          <div className="session-panel" style={{ gridColumn: '1 / -1' }}>
            <div className="session-panel-title">Actividad reciente</div>            {resumenData.presupuestos.length === 0 && resumenData.facturas.length === 0 && (
              <p className="empty" style={{ margin: 0 }}>Sin actividad registrada.</p>
            )}
            {resumenData.presupuestos.slice(0, 3).map(p => (
              <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                <span>Presupuesto #{p.numero || p._id?.slice(-4)}</span>
                <span style={{ color: 'var(--text-soft)' }}>{fmtEuro(p.total)}</span>
              </div>
            ))}
            {resumenData.facturas.slice(0, 3).map(f => (
              <div key={f._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                <span>Factura #{f.numero || f._id?.slice(-4)}</span>
                <span style={{ color: 'var(--text-soft)' }}>{fmtEuro(f.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Presupuestos */}
      {subTab === 'presupuestos' && (
        <div className="session-content">
          {puedeCrearPresupuesto ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <button className="btn btn-primary" onClick={() => abrirRutaDesdeSesion(`/app/presupuestos?clienteId=${sesion.entityId}`)}>Nuevo presupuesto</button>
            </div>
          ) : null}
          {cargandoSub ? <p className="empty">Cargando…</p> : presupuestos.length === 0 ? (
            <p className="empty">Sin presupuestos.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Número</th><th>Fecha</th><th>Estado</th><th>Total</th><th></th></tr>
                </thead>
                <tbody>
                  {presupuestos.map(p => (
                    <tr key={p._id}
                      onDoubleClick={() => abrirItemTab(sesion.id, 'presupuesto', p._id, `Presup. #${p.numero || p._id?.slice(-4)}`)}
                      style={{ cursor: 'pointer' }}>
                      <td>#{p.numero || '—'}</td>
                      <td>{fmtFecha(p.fecha || p.createdAt)}</td>
                      <td>{p.estado || '—'}</td>
                      <td>{fmtEuro(p.total)}</td>
                      <td className="acciones">
                        <button className="btn btn-sm" onClick={() => abrirItemTab(sesion.id, 'presupuesto', p._id, `Presup. #${p.numero || p._id?.slice(-4)}`)}>
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {subTab === 'citas' && (
        <div className="session-content">
          {cargandoSub ? <p className="empty">Cargando…</p> : citas.length === 0 ? <p className="empty">Sin citas vinculadas.</p> : (
            <div className="table-wrap"><table className="data-table"><thead><tr><th>Fecha</th><th>Cita</th><th>Estado</th><th>Notas</th></tr></thead><tbody>
              {citas.map((cita) => {
                const estado = ESTADOS_CITA[cita.estado] || { etiqueta: cita.estado || 'Sin estado', icono: FiCalendar, clase: 'badge' };
                const IconoEstado = estado.icono;
                return <tr key={cita._id}><td>{cita.inicio ? new Date(cita.inicio).toLocaleString('es-ES') : '—'}</td><td>{cita.titulo || '—'}</td><td><span className={`badge ${estado.clase}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><IconoEstado aria-hidden="true" />{estado.etiqueta}</span></td><td>{cita.notas || '—'}</td></tr>;
              })}
            </tbody></table></div>
          )}
        </div>
      )}

      {subTab === 'tareas' && (
        <div className="session-content">
          {puedeCrearTarea ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <button className="btn btn-primary" onClick={() => abrirRutaDesdeSesion(`/app/tareas?clienteId=${sesion.entityId}&accion=voz`)}>Crear tarea por voz</button>
              <button className="btn" onClick={() => abrirRutaDesdeSesion(`/app/tareas?clienteId=${sesion.entityId}&accion=nuevo`)}>Crear tarea manualmente</button>
            </div>
          ) : null}
          {cargandoSub ? <p className="empty">Cargando…</p> : tareas.length === 0 ? <p className="empty">Sin tareas vinculadas.</p> : (
            <div className="table-wrap"><table className="data-table"><thead><tr><th>Tarea</th><th>Estado</th><th>Urgencia</th><th>Fecha tope</th></tr></thead><tbody>
              {tareas.map((t) => <tr key={t._id}><td>{t.titulo}</td><td>{t.estado}</td><td>{t.urgencia}</td><td>{t.fechaTope ? fmtFecha(t.fechaTope) : '—'}</td></tr>)}
            </tbody></table></div>
          )}
        </div>
      )}

      {/* Facturas */}
      {subTab === 'facturas' && (
        <div className="session-content">
          {cargandoSub ? <p className="empty">Cargando…</p> : facturas.length === 0 ? (
            <p className="empty">Sin facturas.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Número</th><th>Fecha</th><th>Total</th><th></th></tr>
                </thead>
                <tbody>
                  {facturas.map(f => (
                    <tr key={f._id}
                      onDoubleClick={() => abrirItemTab(sesion.id, 'factura', f._id, `Factura #${f.numero || f._id?.slice(-4)}`)}
                      style={{ cursor: 'pointer' }}>
                      <td>#{f.numero || '—'}</td>
                      <td>{fmtFecha(f.fecha || f.createdAt)}</td>
                      <td>{fmtEuro(f.total)}</td>
                      <td className="acciones">
                        <button className="btn btn-sm" onClick={() => abrirItemTab(sesion.id, 'factura', f._id, `Factura #${f.numero || f._id?.slice(-4)}`)}>
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Proyectos */}
      {subTab === 'proyectos' && (
        <div className="session-content">
          {cargandoSub ? <p className="empty">Cargando…</p> : proyectos.length === 0 ? (
            <p className="empty">Sin proyectos.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Título</th><th>Tipo</th><th>Estado</th><th></th></tr>
                </thead>
                <tbody>
                  {proyectos.map(p => (
                    <tr key={p._id}
                      onDoubleClick={() => abrirItemTab(sesion.id, 'proyecto', p._id, p.titulo || 'Proyecto')}
                      style={{ cursor: 'pointer' }}>
                      <td>{p.titulo || '—'}</td>
                      <td>{p.tipo || '—'}</td>
                      <td>{p.estado || '—'}</td>
                      <td className="acciones">
                        <button className="btn btn-sm" onClick={() => abrirItemTab(sesion.id, 'proyecto', p._id, p.titulo || 'Proyecto')}>
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Notas */}
      {subTab === 'notas' && (
        <div className="session-content">
          <label className="field">
            <span>Notas del cliente</span>
            <textarea
              rows={8}
              value={notas}
              onChange={e => setNotas(e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </label>
          {notasOk && <div className="alert-ok" style={{ marginTop: 10 }}>Notas guardadas correctamente.</div>}
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-primary" onClick={onGuardarNotas} disabled={guardandoNotas}>
              {guardandoNotas ? 'Guardando…' : 'Guardar notas'}
            </button>
          </div>
        </div>
      )}

      {/* Correos */}
      {subTab === 'correos' && (
        <div className="session-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Emails enviados</span>
            <button className="btn btn-primary" onClick={onAbrirModalEmail} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiMail /> Nuevo email
            </button>
          </div>
          {emailsOk && <div className="alert-ok" style={{ marginBottom: 10 }}>{emailsOk}</div>}
          {emailsError && <div className="alert-error" style={{ marginBottom: 10 }}>{emailsError}</div>}
          {cargandoEmails ? (
            <p className="empty">Cargando…</p>
          ) : emails.length === 0 ? (
            <p className="empty">Sin emails enviados.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Asunto</th><th>Tipo</th><th>Para</th><th>Fecha</th><th>Estado</th><th></th></tr>
                </thead>
                <tbody>
                  {emails.map(em => (
                    <tr key={em._id}
                      onDoubleClick={() => abrirItemTab(sesion.id, 'email', em._id, em.asunto || 'Email')}
                      style={{ cursor: 'pointer' }}>
                      <td>{em.asunto || '—'}</td>
                      <td>
                        <span className={`badge ${{ presupuesto: 'badge-info', factura: 'badge-ok', cita: 'badge-warning' }[em.tipo] || 'badge'}`}>
                          {em.tipo || '—'}
                        </span>
                      </td>
                      <td>{em.para || '—'}</td>
                      <td>{em.creadoEn ? new Date(em.creadoEn).toLocaleDateString('es-ES') : '—'}</td>
                      <td>
                        <span className={`badge ${em.estado === 'enviado' ? 'badge-ok' : em.estado === 'error' ? 'badge-danger' : 'badge'}`}>
                          {em.estado || '—'}
                        </span>
                      </td>
                      <td className="acciones">
                        <button className="btn btn-sm" onClick={() => abrirItemTab(sesion.id, 'email', em._id, em.asunto || 'Email')}>
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}
      </div>

      <ModalEnviarEmailCliente
        abierto={modalEmailAbierto}
        onCerrar={() => setModalEmailAbierto(false)}
        cliente={cliente}
        onEnviado={() => {
          setEmailsOk('Email enviado correctamente.');
          setTimeout(() => setEmailsOk(null), 3000);
          cargarEmails();
        }}
      />

      <Modal abierto={editandoCliente} onCerrar={() => setEditandoCliente(false)} titulo="Editar cliente" ancho={620}>
        {clienteEditado ? <form onSubmit={guardarCliente} className="form-grid">
          {['nombre', 'nif', 'email', 'telefono', 'direccion', 'codigoPostal', 'poblacion', 'provincia'].map((campo) => <label className={`field ${campo === 'nombre' || campo === 'direccion' ? 'span-2' : ''}`} key={campo}><span>{campo}</span><input required={campo === 'nombre'} value={clienteEditado[campo] || ''} onChange={(e) => setClienteEditado((p) => ({ ...p, [campo]: e.target.value }))} /></label>)}
          <label className="field span-2"><span>Notas</span><textarea rows={3} value={clienteEditado.notas || ''} onChange={(e) => setClienteEditado((p) => ({ ...p, notas: e.target.value }))} /></label>
          <div className="form-actions span-2"><button type="button" className="btn" onClick={() => setEditandoCliente(false)}>Cancelar</button><button className="btn btn-primary" disabled={guardandoCliente}><FiSave /> {guardandoCliente ? 'Guardando...' : 'Guardar cambios'}</button></div>
        </form> : null}
      </Modal>
    </div>
  );
}
